// assets/retail-showroom.js
// Showroom eligibility for retail mode: which products may this funeral home show?
//
// How those products are divided into pages is <collection-pager>'s job — this
// script registers itself as that element's eligibility provider (see the
// provider contract in assets/collection-pager.js) and drives it on load, on the
// "show all products" toggle, and on retail/b2b mode changes.
(function() {
  const FALLBACK_API_BASE_URL = 'https://magnolia-memorial-api.vercel.app';
  const DEFAULT_API_BASE_URL = (window.MAGNOLIA && window.MAGNOLIA.apiBaseUrl) || FALLBACK_API_BASE_URL;
  const DEFAULT_VISIBILITY_PATH = '/api/showroom-visibility/';
  const LOADING_LABEL = 'Loading showroom...';

  const state = {
    showAll: false,
    visibilityCache: {},
    initComplete: false
  };

  // Persistent cache system using sessionStorage
  const VisibilityCache = {
    getCacheKey(slug) {
      return `__retail_showroom_cache__${slug}`;
    },
    get(slug) {
      try {
        const key = this.getCacheKey(slug);
        const cached = sessionStorage.getItem(key);
        return cached ? JSON.parse(cached) : null;
      } catch (e) {
        console.warn('[Retail Showroom Cache] Failed to read cache:', e);
        return null;
      }
    },
    set(slug, visibility) {
      try {
        const key = this.getCacheKey(slug);
        sessionStorage.setItem(key, JSON.stringify(visibility));
      } catch (e) {
        console.warn('[Retail Showroom Cache] Failed to write cache:', e);
      }
    },
    has(slug) {
      try {
        const key = this.getCacheKey(slug);
        return sessionStorage.getItem(key) !== null;
      } catch (e) {
        return false;
      }
    },
    clear(slug) {
      try {
        const key = this.getCacheKey(slug);
        sessionStorage.removeItem(key);
      } catch (e) {
        console.warn('[Retail Showroom Cache] Failed to clear cache:', e);
      }
    }
  };

  function getRetailContext() {
    return window.retailContext || {};
  }

  function shouldRun() {
    const ctx = getRetailContext();
    return ctx.mode === 'retail' && !!ctx.slug;
  }

  function getPager() {
    return window.CollectionPager ? window.CollectionPager.current() : null;
  }

  function getToggleInput() {
    return document.querySelectorAll('#RetailShowroomToggle, #RetailShowroomToggleMobile');
  }

  function getVisibilityEndpoint(slug) {
    const config = window.retailShowroomConfig || {};
    if (config.visibilityEndpoint) return config.visibilityEndpoint;
    const baseUrl = config.apiBaseUrl || DEFAULT_API_BASE_URL;
    return `${baseUrl}${DEFAULT_VISIBILITY_PATH}${slug}`;
  }

  // skus: array of SKU strings
  // slug: funeral home slug for API
  // options.force (boolean): if true always hit the network, ignoring existing cache
  async function fetchVisibilityMap(skus, slug, options = {}) {
    const { force = false } = options;
    if (!skus.length || !slug) return {};

    const cached = {};
    const missing = [];
    skus.forEach((sku) => {
      if (!force && state.visibilityCache[sku] !== undefined) {
        cached[sku] = state.visibilityCache[sku];
      } else {
        missing.push(sku);
      }
    });

    if (missing.length === 0) return cached;

    const endpoint = getVisibilityEndpoint(slug);
    let response;
    try {
      response = await fetch(endpoint);
    } catch (e) {
      console.warn('[Retail Showroom] Visibility network request failed:', e);
      return cached;
    }

    if (!response.ok) {
      console.warn('[Retail Showroom] Visibility request failed with status', response.status);
      return cached;
    }

    const payload = await response.json();
    const data = payload.data || payload.visibility || {};

    if (Array.isArray(data)) {
      data.forEach((row) => {
        if (row && row.sku) {
          state.visibilityCache[row.sku] = Boolean(row.displayInShowroom ?? row.display_in_showroom ?? row.visible);
        }
      });
    } else if (data && typeof data === 'object') {
      Object.entries(data).forEach(([sku, value]) => {
        state.visibilityCache[sku] = Boolean(value);
      });
    }

    const merged = { ...cached };
    missing.forEach((sku) => {
      merged[sku] = state.visibilityCache[sku] ?? false;
    });

    // Update persistent cache with full visibility map
    const persistedCache = VisibilityCache.get(slug) || {};
    Object.assign(persistedCache, state.visibilityCache);
    VisibilityCache.set(slug, persistedCache);

    // When force=true, return the complete visibility map for all products,
    // not just the subset of SKUs that were requested. This ensures that the
    // eligibility pass gets the full map to correctly judge all items.
    if (force) {
      return state.visibilityCache;
    }

    return merged;
  }

  function skuOf(item) {
    const el = item.querySelector('[data-product-sku]');
    return el ? el.dataset.productSku : null;
  }

  function skusOf(items) {
    return items.map(skuOf).filter(Boolean);
  }

  // Eligibility provider for <collection-pager> — the whole of this script's
  // contribution to what the shopper sees. The pager asks, we answer with a
  // subset of the items it handed us; page numbers are none of our business.
  const showroomEligibility = {
    isActive: shouldRun,
    loadingLabel: LOADING_LABEL,
    async eligible(items, options = {}) {
      const ctx = getRetailContext();
      const visibility = await fetchVisibilityMap(skusOf(items), ctx.slug, { force: !!options.force });
      const hasAny = Object.values(visibility).some((v) => v === true);

      // A funeral home that has marked nothing gets the whole catalog rather
      // than an empty showroom.
      if (!hasAny) return null;

      return items.filter((item) => visibility[skuOf(item)] === true);
    }
  };

  // Retail prices have to be applied to cards the pager has just revealed —
  // including ones it pulled in from later network pages. initRetailBranding is
  // idempotent and uses cached prices, so this is fast.
  function applyRetailPrices() {
    if (typeof window.initRetailBranding === 'function') {
      window.initRetailBranding();
    }
  }

  function applyShowroom(options = {}) {
    if (!shouldRun()) return;
    const pager = getPager();
    if (!pager) return;
    if (state.showAll) return pager.showEverything();
    return pager.refresh({ force: !!options.forceRefresh });
  }

  function bindToggle() {
    const toggles = getToggleInput();
    if (!toggles || toggles.length === 0) return;

    toggles.forEach((toggle) => {
      if (toggle.dataset.showroomBound === 'true') return;
      toggle.addEventListener('change', () => {
        state.showAll = toggle.checked;
        syncToggleState();
        // Fire async but don't wait - the grid already exists at this point.
        // Use cached data, no need to force refresh on toggle
        applyShowroom({ forceRefresh: false });
      });
      toggle.dataset.showroomBound = 'true';
    });
  }

  function syncToggleState() {
    const toggles = getToggleInput();
    if (!toggles || toggles.length === 0) return;
    toggles.forEach((toggle) => {
      toggle.checked = state.showAll;
    });
  }

  function pageSkus() {
    const grid = document.getElementById('ProductGridContainer');
    if (!grid) return [];
    return Array.from(grid.querySelectorAll('[data-product-sku]'))
      .map((el) => el.dataset.productSku)
      .filter(Boolean);
  }

  // Initialize cache from current page's products on page load
  async function cacheCurrentPageVisibility() {
    const ctx = getRetailContext();
    if (!ctx.slug) return;

    // Always attempt to refresh visibility for products on the current page. We
    // will still load any existing cache immediately so users don't see a
    // flash-of-nothing, but the following fetch ensures that the cache gets
    // updated whenever the showroom selections change in the back end.

    if (VisibilityCache.has(ctx.slug)) {
      const cached = VisibilityCache.get(ctx.slug) || {};
      Object.assign(state.visibilityCache, cached);
      // note: continue to fetch below to refresh the cache
    }

    const skus = pageSkus();
    if (skus.length === 0) return;

    try {
      // always refresh from server so cache stays current
      await fetchVisibilityMap(skus, ctx.slug, { force: true });
    } catch (e) {
      console.warn('[Retail Showroom] Failed to cache current page visibility:', e);
    }
  }

  async function init() {
    if (window.CollectionPager) {
      window.CollectionPager.setEligibilityProvider(showroomEligibility);
    }
    document.addEventListener('collection-pager:render', applyRetailPrices);

    // Cache current page visibility on all page loads
    await cacheCurrentPageVisibility();

    // Mark init complete so handleModeChange can run after this point
    // This must happen regardless of mode so that future mode changes can process
    state.initComplete = true;

    if (!shouldRun()) return;

    state.showAll = false;
    syncToggleState();
    bindToggle();
    // On page load, wait for filtering to complete (the pager may wait for the
    // product list to render). Force refresh to ensure fresh data from the API.
    await applyShowroom({ forceRefresh: true });
  }

  function handleModeChange(mode) {
    const pager = getPager();

    if (mode !== 'retail') {
      state.showAll = false;
      syncToggleState();
      // Not release() — display pagination belongs to every shopper, wholesale
      // included. refresh() with no active provider re-divides the grid
      // unfiltered, which is exactly what leaving retail mode means.
      if (pager) pager.refresh();
      return;
    }

    // Skip applying eligibility during initial load (before init completes);
    // init handles the initial pass. Only apply when mode changes AFTER init
    // (e.g. the user toggling retail mode on).
    if (!state.initComplete) return;

    state.showAll = false;
    syncToggleState();
    bindToggle();
    if (pager) pager.hideAll();
    // Fire async but don't wait - the grid already exists when mode is toggled.
    // Force refresh since we may not have cached all products (especially on
    // the initial toggle from b2b)
    applyShowroom({ forceRefresh: true });
  }

  window.RetailShowroom = {
    refresh() {
      if (!shouldRun()) return;
      bindToggle();
      // Fire async but don't wait - force refresh to get latest data
      applyShowroom({ forceRefresh: true });
    },
    handleModeChange
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
